import { useEffect, useState } from "react";
import { Box, Button, Dialog } from "@mui/material";
import AlbumOutlinedIcon from "@mui/icons-material/AlbumOutlined";
import {
  APP_COLOR,
  APP_HOVER_COLOR,
  BUTTON_TEXT_COLOR,
  FONT_FAMILY,
} from "../../theme";

export interface ClaimMigrateSummary {
  tracks: number;
  projects: number;
  mbUsed: number;
}

export interface ClaimMigrateDialogProps {
  open: boolean;
  summary: ClaimMigrateSummary;
  onContinue: () => void;
  autoContinueMs?: number;
}

const SURFACE = "#151718";
const BORDER = "#26292B";
const BORDER_SOFT = "#1c1f24";
const MUTED = "#889096";
const FAINT = "#5b626a";
const BG = "#0C111D";
const SURFACE_2 = "#1b1e26";

const ClaimMigrateDialog: React.FC<ClaimMigrateDialogProps> = ({
  open,
  summary,
  onContinue,
  autoContinueMs = 4000,
}) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) {
      setElapsed(0);
      return;
    }
    const start = performance.now();
    let rafId: number;
    const tick = (now: number) => {
      const e = Math.min(autoContinueMs, now - start);
      setElapsed(e);
      if (e >= autoContinueMs) {
        onContinue();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [open, autoContinueMs, onContinue]);

  const progress = autoContinueMs > 0 ? (elapsed / autoContinueMs) * 100 : 0;
  const secondsLeft = Math.max(0, Math.ceil((autoContinueMs - elapsed) / 1000));

  return (
    <Dialog
      open={open}
      onClose={onContinue}
      aria-labelledby="claim-migrate-headline"
      PaperProps={{
        sx: {
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: "20px",
          boxShadow: "0 50px 130px -45px rgba(0,0,0,0.9)",
          width: 520,
          maxWidth: "calc(100% - 32px)",
          overflow: "hidden",
          fontFamily: FONT_FAMILY,
        },
      }}
    >
      <Box sx={{ padding: "36px 38px 30px", textAlign: "center" }}>
        <Box
          sx={{
            width: 76,
            height: 76,
            margin: "0 auto 20px",
            borderRadius: "50%",
            background: "rgba(39,182,203,0.14)",
            border: "1px solid rgba(39,182,203,0.32)",
            color: APP_COLOR,
            display: "grid",
            placeItems: "center",
          }}
        >
          <AlbumOutlinedIcon sx={{ fontSize: 36 }} />
        </Box>
        <Box
          id="claim-migrate-headline"
          sx={{
            color: "#FFFFFF",
            fontSize: 23,
            fontWeight: 700,
            letterSpacing: "-0.015em",
            fontFamily: FONT_FAMILY,
          }}
        >
          Your tracks made it in
        </Box>
        <Box
          sx={{
            color: MUTED,
            fontSize: 14.5,
            lineHeight: 1.55,
            margin: "11px auto 0",
            maxWidth: 400,
            fontFamily: FONT_FAMILY,
          }}
        >
          Everything you uploaded as a guest has been added to your new account.
        </Box>
        <Box sx={{ display: "flex", gap: "10px", justifyContent: "center", margin: "22px 0 6px" }}>
          {[
            { n: summary.tracks, l: summary.tracks === 1 ? "track" : "tracks" },
            { n: summary.projects, l: summary.projects === 1 ? "project" : "projects" },
            { n: summary.mbUsed, l: "MB used" },
          ].map((item) => (
            <Box
              key={item.l}
              sx={{
                flex: 1,
                maxWidth: 130,
                background: BG,
                border: `1px solid ${BORDER_SOFT}`,
                borderRadius: "12px",
                padding: "14px 10px",
              }}
            >
              <Box sx={{ color: "#FFFFFF", fontSize: 22, fontWeight: 700 }}>{item.n}</Box>
              <Box sx={{ color: MUTED, fontSize: 12, marginTop: "2px" }}>{item.l}</Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "11px", marginTop: "26px" }}>
          <Button
            onClick={onContinue}
            sx={{
              background: APP_COLOR,
              color: BUTTON_TEXT_COLOR,
              fontFamily: FONT_FAMILY,
              fontSize: 15,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              padding: "13px",
              "&:hover": { background: APP_HOVER_COLOR },
            }}
          >
            Continue to dashboard
          </Button>
        </Box>
      </Box>
      <Box aria-hidden sx={{ height: 4, background: SURFACE_2 }}>
        <Box sx={{ height: "100%", width: `${progress}%`, background: APP_COLOR }} />
      </Box>
      <Box
        sx={{
          textAlign: "center",
          color: FAINT,
          fontSize: 12,
          padding: "10px 0",
          fontFamily: FONT_FAMILY,
        }}
      >
        Continuing automatically in {secondsLeft}s…
      </Box>
    </Dialog>
  );
};

export default ClaimMigrateDialog;
